
export enum TranscriptionStatus {
  IDLE,
  UPLOADING, // This now represents "Queued"
  PROCESSING,
  SUCCESS,
  ERROR,
}

/** Firestore: users/{uid} */
export interface UserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  isPaid: boolean;
  plan: 'free' | 'lifetime';
  stripeCustomerId?: string;
  credits: number; // remaining transcription time in ms
  createdAt: number;
  lastSeen: number;
  changelogReadAt?: number;
}

/** Firestore: users/{uid}/folders/{folderId} */
export interface Folder {
  id: string;
  name: string;
  createdAt: number;
  color?: string;
}

/** Firestore: users/{uid}/transcripts/{transcriptId}/versions/{versionId} */
export interface TranscriptVersion {
  id: string;
  transcript: string;
  savedAt: number;
  label?: string;
}

/** Firestore: users/{uid}/transcripts/{transcriptId}/comments/{commentId} */
export interface TranscriptComment {
  id: string;
  text: string;
  timestamp: number; // media timestamp in ms the comment anchors to
  createdAt: number;
  authorUid: string;
}

export interface TranscriptionHistoryItem {
  id: string;
  fileName: string;
  transcript: string;
  timestamp: number;
  duration: number; // in milliseconds
  mediaData?: string; // Base64 encoded media data (for small files/offline)
  mediaType?: string; // MIME type of the media
  storageUrl?: string; // Firebase Storage Download URL (for large files)
  shareId?: string; // 10-char random ID for public share link at /s/[shareId]
  folderId?: string | null; // Folder assignment (null = root/unassigned)
  originalTranscript?: string; // Original-language transcript when translate mode is used
}

export interface TranscriptionJob {
  id: string;
  file: File;
  fileName: string;
  status: TranscriptionStatus;
  transcript?: string;
  partialTranscript?: string; // Streaming chunks from local Whisper worker
  error?: string;
  startTime?: number; // timestamp when processing starts
  mediaDuration?: number; // Duration of the media in milliseconds
  isBackground?: boolean; // True for files >100 MB — processed in background
  progress?: number; // 0–100 estimated progress for background jobs
  retryCount?: number; // Number of retry attempts made so far (max 3)
  engineOverride?: 'cloud' | 'local'; // Per-job engine override for fallback
  folderId?: string | null; // Folder to save transcript into
  wordTimestamps?: Array<{ word: string; start: number; end: number }>; // Word-level timestamps
  speakerLabels?: Record<string, string>; // Speaker ID → display name mapping
  originalTranscript?: string; // Original-language transcript when translate mode is used
}
